import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Button, CircularProgress, Fade, FormHelperText, Typography } from '@mui/material';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';
import { useStationsFilter } from '@/hooks/useStationsFilter';

import { handleError, isResponseError } from '@/utils/errorUtils';

import type { TEntityOption } from '@/components/filter/types';
import { Cross } from '@/components/icons/Cross';
import { IconTooltipButton } from '@/components/iconTooltipButton/IconTooltipButton';
import { GenericModal } from '@/components/modal/GenericModal';
import { GenericModalClose } from '@/components/modal/GenericModalClose';

import { useImportCatalog } from '@/mutations/catalog';

import { styles } from './UploadCatalog.styles';
import { UploadCatalogStationSelect } from './UploadCatalogStationSelect';
import {
  CATALOG_UPLOAD_ACCEPT,
  extractCatalogUploadErrorMessage,
  getCatalogFileKind,
  isCatalogSpreadsheet,
} from './utils';

export type TUploadCatalogProps = TModalsContext & {
  stationIds?: string[];
  /**
   * Keeps the site fixed to the first of `stationIds` (read-only Site field). Set when the popup
   * opens from a station's Products tab; the Product Catalog page leaves it off and lets the
   * operator pick any site.
   *
   * `stationIds` only preselects the popup's site (the first one). A site picked inside the popup
   * is the popup's own: it is never written to the shared site selection.
   */
  lockStation?: boolean;
  onStarted: (requestId: string, storeId: string) => void;
};

const MODAL_NAME = 'productCatalog.upload';

export const UploadCatalog = ({
  close,
  lock,
  stationIds: initialStationIds,
  lockStation = false,
  onStarted,
}: TUploadCatalogProps) => {
  const { t } = useTranslation();
  const { lookupStations, restoreStations } = useStationsFilter('portalScope');
  const { mutateAsync: startImport } = useImportCatalog();

  const [station, setStation] = useState<TEntityOption | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const initialStationId = initialStationIds?.find(Boolean);

  /**
   * True only while the preselected site (`initialStationId`) is being fetched; drives the
   * spinner in the Site field. Starts true when there is a site to restore, so the first paint
   * already shows the spinner instead of a blank field.
   */
  const [restoringStation, setRestoringStation] = useState(Boolean(initialStationId));

  /**
   * Set once the operator picks a site by hand. A restore that resolves after that must not
   * overwrite the pick (unlocked popup on the Product Catalog page), so the effect checks it.
   */
  const pickedByOperator = useRef(false);

  // The latest `restoreStations`, read inside the effect below. Its identity changes whenever the
  // portal's site names refresh (a station was renamed or created); the effect must not restore
  // again for that.
  const restoreStationsRef = useRef(restoreStations);
  restoreStationsRef.current = restoreStations;

  /**
   * Restores the preselected site's name, once per preselected site id. Runs outside the portal
   * filter context, so `restoreStations` asks the backend (the saved station queries answer at once
   * for a site seen before). Does not touch the shared site selection: the
   * caller already holds this site. On failure or an empty result the
   * field stays empty (Continue stays disabled) and the error is only logged.
   */
  useEffect(() => {
    if (!initialStationId) {
      setRestoringStation(false);
      return;
    }

    let relevant = true;
    setRestoringStation(true);
    Promise.resolve(restoreStationsRef.current([initialStationId]))
      .then((options) => {
        if (!relevant || pickedByOperator.current) {
          return;
        }

        if (options[0]) {
          setStation(options[0]);
        }
        setRestoringStation(false);
      })
      .catch((err) => {
        handleError(err, 'UNHANDLED_RESPONSE');

        if (relevant) {
          setRestoringStation(false);
        }
      });

    return () => {
      relevant = false;
    };
  }, [initialStationId]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    multiple: false,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    onDropAccepted: useCallback((files: File[]) => {
      setError(null);
      setFile(files[0]);
    }, []),
    onDropRejected: useCallback(() => {
      setFile(null);
      setError(t('productCatalog.import.invalidFile'));
    }, [t]),
  });

  const handleClose = () => {
    if (!loading) {
      close(MODAL_NAME);
    }
  };

  const handleStationChange = (next: TEntityOption | null) => {
    // The operator's pick wins over a restore still in flight: stop the spinner and ignore it.
    pickedByOperator.current = true;
    setRestoringStation(false);
    // Popup-only: the pick decides where this import goes and changes nothing outside the popup.
    setStation(next);
    setError(null);
  };

  const submit = () => {
    if (!station || !file || !isCatalogSpreadsheet(file)) {
      setError(
        !station
          ? t('productCatalog.import.selectStation')
          : t('productCatalog.import.invalidFile'),
      );
      return;
    }

    const storeId = station.id;
    const selectedFile = file;

    setLoading(true);
    setError(null);
    lock(MODAL_NAME, () => false);

    // Starts the import job only. The catalog list refreshes when the page's polling sees it end.
    startImport({ file: selectedFile, stationId: storeId })
      .then((job) => {
        onStarted(job.requestId, storeId);
        close(MODAL_NAME);
      })
      .catch((err) => {
        handleError(err, 'UNHANDLED_RESPONSE');
        const backendMessage = isResponseError(err)
          ? extractCatalogUploadErrorMessage(err.response.data?.message)
          : '';
        setError(backendMessage || t('productCatalog.import.failedRequest'));
        setLoading(false);
        lock(MODAL_NAME, () => true);
      });
  };

  return (
    <GenericModal sx={styles.modal}>
      <GenericModalClose onClick={handleClose} disabled={loading} />

      <Box sx={styles.header}>
        <Typography sx={styles.title}>{t('productCatalog.uploadCatalog')}</Typography>
        <Typography sx={styles.description}>{t('productCatalog.import.description')}</Typography>
      </Box>

      <Box sx={styles.body}>
        <UploadCatalogStationSelect
          value={station}
          getOptions={lookupStations}
          onChange={handleStationChange}
          disabled={loading}
          locked={lockStation}
          loading={restoringStation}
        />

        <Box {...getRootProps()} sx={styles.dropZone}>
          <input {...getInputProps()} accept={CATALOG_UPLOAD_ACCEPT} />
          <Typography sx={styles.dropZoneText}>
            <Trans i18nKey="productCatalog.import.browseOrDrop">
              <Typography
                component="button"
                type="button"
                onClick={() => open()}
                sx={styles.browseButton}
              />
            </Trans>
          </Typography>

          <Fade in={isDragActive}>
            <Box sx={styles.dropZoneOverlay}>
              <Typography variant="body2">{t('productCatalog.import.dropHere')}</Typography>
            </Box>
          </Fade>
        </Box>

        {file && (
          <Box sx={styles.fileChip}>
            <Box sx={styles.fileKind}>
              <Typography sx={styles.fileKindLabel}>{getCatalogFileKind(file)}</Typography>
            </Box>
            <Typography variant="body2" noWrap sx={styles.fileName}>
              {file.name}
            </Typography>
            <IconTooltipButton
              size="small"
              label={t('common.removeFile')}
              onClick={() => setFile(null)}
              disabled={loading}
              sx={styles.fileRemove}
            >
              <Cross fontSize="small" />
            </IconTooltipButton>
          </Box>
        )}

        {error && <FormHelperText error>{error}</FormHelperText>}
      </Box>

      <Box sx={styles.footer}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={loading}
          sx={styles.cancelButton}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={loading || !station || !file}
          sx={styles.continueButton}
        >
          {loading ? t('productCatalog.import.uploading') : t('common.continue')}
          {loading && <CircularProgress size={18} />}
        </Button>
      </Box>
    </GenericModal>
  );
};
